import { Component, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { Artikl, Opi, ApiClientService, KretanjeCijena, Cijene } from '../../services/api-client.service';
import { CacheService } from '../../services/cache.service';

@Component({
  selector: 'app-kretanje-cijena',
  templateUrl: './kretanje-cijena.component.html',
  styleUrls: ['./kretanje-cijena.component.scss']
})
export class KretanjeCijenaComponent {
  drzavaOptions = [
    { value: 'biH', text: 'Bosna i Hercegovina' },
    { value: 'hrv', text: 'Hrvatska' },
    { value: 'srb', text: 'Srbija' },
    { value: 'ino', text: 'Inostranstvo' }
  ];
  @ViewChild('chart', { static: false }) chart: any;

  // Form
  filterForm!: FormGroup;
  loading = false;

  // Data
  chartData: any[] = [];
  artikli: Artikl[] = [];
  opisi: Opi[] = [];
  selectedOpisi: number[] = [];
  selectedArtiklId: number | null = null;

  // Chart properties
  yAxisTitle: string = 'Cijena';

  constructor(
    private apiClient: ApiClientService,
    private fb: FormBuilder,
    private cache: CacheService
  ) {
    this.customizeTooltip = this.customizeTooltip.bind(this);
    this.getValutaThing = this.getValutaThing.bind(this);
    this.initializeForm();
  }

  async ngOnInit() {
    await this.loadLookupData();
  }

  initializeForm() {
    const today = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(today.getMonth() - 1);

    this.filterForm = this.fb.group({
      drzava: ['biH'],
      dateFrom: [oneMonthAgo],
      dateTo: [today],
      artikal: [null]
    });
  }

  async loadLookupData() {
    try {
      const [artikliData, opisiData] = await Promise.all([
        this.cache.getCachedOrFetch('artikli', () => this.apiClient.artikliAll()),
        this.cache.getCachedOrFetch('opisi', () => this.apiClient.opisiAll())
      ]);
      this.artikli = artikliData;
      this.opisi = opisiData;
    } catch (error) {
      console.error('Error loading lookup data:', error);
    }
  }

  onArtiklChange(event: any) {
    this.selectedArtiklId = event.value;
    this.selectedOpisi = []; // Reset selected opisi when artikl changes
  }

  onOpisChange(event: any) {
    this.selectedOpisi = event.value || [];
  }

  async onSearch() {
    if (this.filterForm.valid) {
      this.loading = true;
      try {
        const formData = this.filterForm.value;
        this.selectedArtiklId = formData.artikal;
        
        let result: KretanjeCijena = {} as KretanjeCijena;

        // Call appropriate endpoint with opisi parameter
        switch (formData.drzava) {
          case 'biH':
            result = await this.apiClient.biH2(formData.dateFrom, formData.dateTo, formData.artikal, this.selectedOpisi);
            break;
          case 'hrv':
            result = await this.apiClient.hrv2(formData.dateFrom, formData.dateTo, formData.artikal, this.selectedOpisi);
            break;
          case 'srb':
            result = await this.apiClient.srb2(formData.dateFrom, formData.dateTo, formData.artikal, this.selectedOpisi);
            break;
          case 'ino':
            result = await this.apiClient.ino2(formData.dateFrom, formData.dateTo, formData.artikal, this.selectedOpisi);
            break;
          default:
            result = await this.apiClient.biH2(formData.dateFrom, formData.dateTo, formData.artikal, this.selectedOpisi);
            break;
        }

        this.processChartData(result);

      } catch (error) {
        console.error('Error loading price data:', error);
      } finally {
        this.loading = false;
      }
    }
  }

  processChartData(kretanjeCijena: KretanjeCijena) {
    this.chartData = [];

    if (kretanjeCijena.cijene) {
      // Convert the object to array for chart
      Object.keys(kretanjeCijena.cijene).forEach(dateKey => {
        if (kretanjeCijena.cijene != null) {
          const cijene: Cijene = kretanjeCijena.cijene[dateKey];
          this.chartData.push({
            date: new Date(dateKey),
            min: cijene.min,
            avg: cijene.avg,
            max: cijene.max,
            artikl: cijene.artikl,
            opis: cijene.opis,
            valutaThing: cijene.valutaThing
          });
        }
      });

      // Sort by date
      this.chartData.sort((a, b) => a.date.getTime() - b.date.getTime());

      // Update chart properties with returned data
      this.updateChartProperties(kretanjeCijena);
    }
  }

  updateChartProperties(kretanjeCijena: KretanjeCijena) {
    this.yAxisTitle = kretanjeCijena.yAxis || 'Cijena';
  }

  onExport() {
    if (this.chart && this.chart.instance) {
      this.chart.instance.exportTo('PDF', 'KretanjeCijena');
    }
  }

  customizeTooltip(pointInfo: any) {
    return {
      text: `${pointInfo.valueText} ${this.yAxisTitle}`
    };
  };

  getValutaThing(rowData: any): string {
    return this.yAxisTitle;
  }

  getArtiklName(artiklId: number | null): string {
    const artikl = this.artikli.find(a => a.id === artiklId);
    return artikl ? artikl.artikl1 as string : `Artikl #${artiklId}`;
  }

  getOpisNames(opisiIds: number[]): string {
    if (!opisiIds || opisiIds.length === 0) return 'SVE';

    const opisNames = opisiIds.map(id => {
      const opis = this.opisi.find(o => o.id === id);
      return opis ? opis.opis : `Opis #${id}`;
    });

    return opisNames.join(', ');
  }
}